import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { householdService } from '../../services/householdService';
import { robojacketsTheme } from '../../theme/robojacketsTheme';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { 
  trackUIInteraction, 
  trackAPICall, 
  logger, 
  trackError 
} from '../../utils/sentryUtils';

const Households = () => {
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHousehold, setEditingHousehold] = useState(null);
  const [viewingHousehold, setViewingHousehold] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [householdToDelete, setHouseholdToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    addressStreet: '',
    addressCity: '',
    addressZip: '',
    isActive: true,
  });
  const [selectedCaregiverId, setSelectedCaregiverId] = useState(null);
  const [savingPrimary, setSavingPrimary] = useState(false);
  
  // Search states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    loadHouseholds();
  }, []);

  const loadHouseholds = async () => {
    try {
      setLoading(true);
      const data = await householdService.getAllHouseholds();
      setHouseholds(data);
    } catch (err) {
      setError('Failed to load households');
      console.error('Error loading households:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (household = null) => {
    if (household) {
      setEditingHousehold(household);
      setFormData({
        name: household.name,
        addressStreet: household.addressStreet || '',
        addressCity: household.addressCity || '',
        addressZip: household.addressZip || '',
        isActive: household.isActive,
      });
    } else {
      setEditingHousehold(null);
      setFormData({
        name: '',
        description: '',
        addressStreet: '',
        addressCity: '',
        addressZip: '',
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingHousehold(null);
    setFormData({
      name: '',
      addressStreet: '',
      addressCity: '',
      addressZip: '',
      isActive: true,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingHousehold) {
        await householdService.updateHousehold(editingHousehold.id, formData);
      } else {
        await householdService.createHousehold(formData);
      }
      handleCloseDialog();
      loadHouseholds();
    } catch (err) {
      setError('Failed to save household');
      console.error('Error saving household:', err);
    }
  };

  const handleDelete = async (householdId) => {
    const household = households.find(h => h.id === householdId);
    setHouseholdToDelete(household);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!householdToDelete) return;
    
    try {
      await householdService.deleteHousehold(householdToDelete.id);
      setSuccess(`Household "${householdToDelete.name}" has been deleted successfully.`);
      setTimeout(() => setSuccess(''), 5000); // Clear success message after 5 seconds
      loadHouseholds();
    } catch (err) {
      setError('Failed to delete household');
      console.error('Error deleting household:', err);
    } finally {
      setDeleteDialogOpen(false);
      setHouseholdToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setHouseholdToDelete(null);
  };

  const handleRegenerateCode = async (householdId) => {
    if (window.confirm('Are you sure you want to regenerate the household code? This will invalidate the current code.')) {
      try {
        const updatedHousehold = await householdService.regenerateHouseholdCode(householdId);
        setHouseholds(households.map(h => h.id === householdId ? updatedHousehold : h));
        setSuccess(`Household code regenerated successfully! New code: ${updatedHousehold.householdCode}`);
        setTimeout(() => setSuccess(''), 5000); // Clear success message after 5 seconds
      } catch (err) {
        setError('Failed to regenerate household code');
        console.error('Error regenerating household code:', err);
      }
    }
  };

  const handleViewHousehold = async (household) => {
    try {
      const detailedHousehold = await householdService.getHouseholdDetailById(household.id);
      setViewingHousehold(detailedHousehold);
      setSelectedCaregiverId(detailedHousehold.primaryCaregiverId || null);
    } catch (err) {
      setError('Failed to load household details');
      console.error('Error loading household details:', err);
    }
  };

  const handleCloseViewDialog = () => {
    setViewingHousehold(null);
  };

  const handleSavePrimaryCaregiver = async () => {
    if (!viewingHousehold || !selectedCaregiverId) return;
    setSavingPrimary(true);
    try {
      await trackUIInteraction('Set Primary Caregiver', async () => {
        logger.info(
          logger.fmt`Setting primary caregiver for household ${viewingHousehold.id} to user ${selectedCaregiverId}`
        );
        const updated = await trackAPICall(
          'PUT',
          `/households/${viewingHousehold.id}/primary-caregiver/${selectedCaregiverId}`,
          async () => {
            return householdService.updatePrimaryCaregiver(viewingHousehold.id, selectedCaregiverId);
          },
          { householdId: viewingHousehold.id, caregiverId: selectedCaregiverId }
        );
        setViewingHousehold({ ...viewingHousehold, primaryCaregiverId: selectedCaregiverId });
        setHouseholds(households.map(h => h.id === viewingHousehold.id ? { ...h, primaryCaregiverId: selectedCaregiverId } : h));
        setSuccess('Primary caregiver updated');
        setTimeout(() => setSuccess(''), 4000);
        return updated;
      });
    } catch (err) {
      logger.error('Failed to set primary caregiver', { error: err?.message });
      trackError(err, { action: 'set_primary_caregiver', householdId: viewingHousehold?.id });
      setError('Failed to update primary caregiver');
    } finally {
      setSavingPrimary(false);
    }
  };

  const formatAddress = (household) => {
    const parts = [
      household.addressStreet,
      household.addressCity,
      household.addressZip
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'No address provided';
  };

  // Filter households based on search term and status
  const filteredHouseholds = households.filter(household => {
    const matchesSearch = !searchTerm || 
      household.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      household.householdCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatAddress(household).toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !filterStatus || 
      (filterStatus === 'active' && household.isActive) ||
      (filterStatus === 'inactive' && !household.isActive);
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Households Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ minWidth: 120 }}
        >
          Add Household
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search and Filter Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Households"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, code, or address..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Filter by Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>

                <TableCell>Address</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHouseholds.map((household) => (
                <TableRow key={household.id} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {household.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {household.householdCode}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatAddress(household)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {household.userIds ? household.userIds.length : 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={household.isActive ? 'Active' : 'Inactive'}
                      color={household.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {household.createdAt ? new Date(household.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <IconButton
                        size="small"
                        onClick={() => handleViewHousehold(household)}
                        color="primary"
                        title="View Details"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(household)}
                        color="primary"
                        title="Edit Household"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleRegenerateCode(household.id)}
                        color="warning"
                        title="Regenerate Code"
                      >
                        <RefreshIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(household.id)}
                        color="error"
                        title="Delete Household"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {filteredHouseholds.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No households found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || filterStatus ? 'Try adjusting your search criteria.' : 'Create your first household to get started.'}
          </Typography>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle 
          sx={{
            backgroundColor: robojacketsTheme.palette.primary.main,
            color: 'white',
            fontWeight: 'bold'
          }}
        >
          {editingHousehold ? 'Edit Household' : 'Add New Household'}
        </DialogTitle>
        <DialogContent sx={{ pt: 6 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Household Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2, mt: 2 }}
              required
            />

            <TextField
              fullWidth
              label="Street Address"
              value={formData.addressStreet}
              onChange={(e) => setFormData({ ...formData, addressStreet: e.target.value })}
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.addressCity}
                  onChange={(e) => setFormData({ ...formData, addressCity: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={formData.addressZip}
                  onChange={(e) => setFormData({ ...formData, addressZip: e.target.value })}
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              backgroundColor: robojacketsTheme.palette.primary.main,
              '&:hover': {
                backgroundColor: robojacketsTheme.palette.primary.dark
              }
            }}
          >
            {editingHousehold ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog 
        open={!!viewingHousehold} 
        onClose={handleCloseViewDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle 
          sx={{
            backgroundColor: robojacketsTheme.palette.primary.main,
            color: 'white',
            fontWeight: 'bold'
          }}
        >
          Household Details
        </DialogTitle>
        <DialogContent sx={{ pt: 6 }}>
          {viewingHousehold && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {viewingHousehold.name}
              </Typography>

              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Address:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatAddress(viewingHousehold)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={2} alignItems="center">
                    <Typography variant="body2" fontWeight="medium">
                      Status:
                    </Typography>
                    <Chip
                      label={viewingHousehold.isActive ? 'Active' : 'Inactive'}
                      color={viewingHousehold.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={2} alignItems="center">
                    <Typography variant="body2" fontWeight="medium">
                      Household Code:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {viewingHousehold.householdCode}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="primary-caregiver-select-label">Primary Caregiver</InputLabel>
                    <Select
                      labelId="primary-caregiver-select-label"
                      id="primary-caregiver-select"
                      value={selectedCaregiverId || ''}
                      label="Primary Caregiver"
                      onChange={(e) => setSelectedCaregiverId(e.target.value || null)}
                    >
                      {Array.isArray(viewingHousehold.users) && viewingHousehold.users
                        .filter(u => Array.isArray(u.roleNames) ? u.roleNames.includes('Caregiver') : (u.roleNames && u.roleNames.has && u.roleNames.has('Caregiver')))
                        .map(u => (
                          <MenuItem key={u.id} value={u.id}>
                            {(u.getDisplayName ? u.getDisplayName() : `${u.firstName} ${u.lastName}`) || u.email}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button 
                    variant="contained" 
                    onClick={handleSavePrimaryCaregiver}
                    disabled={!selectedCaregiverId || savingPrimary}
                    sx={{ minWidth: 180 }}
                  >
                    {savingPrimary ? 'Saving...' : 'Save Primary'}
                  </Button>
                </Grid>
              </Grid>
              
              {/* Members Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon />
                  Members ({viewingHousehold.users ? viewingHousehold.users.length : 0})
                </Typography>
                
                {viewingHousehold.users && viewingHousehold.users.length > 0 ? (
                  <Grid container spacing={2}>
                    {viewingHousehold.users.map((user) => (
                      <Grid item xs={12} md={6} key={user.id}>
                        <Paper elevation={1} sx={{ p: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                              <Typography variant="body1" fontWeight="medium" gutterBottom>
                                {user.getDisplayName ? user.getDisplayName() : `${user.firstName} ${user.lastName}`}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {user.email}
                              </Typography>
                              {user.roleNames && user.roleNames.length > 0 && (
                                <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                  {user.roleNames.map((roleName) => (
                                    <Chip
                                      key={roleName}
                                      label={roleName}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              )}
                              {user.graduationYear && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  Graduation: {user.graduationYear}
                                </Typography>
                              )}
                              {user.school && (
                                <Typography variant="body2" color="text.secondary">
                                  School: {user.school}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              label={user.isActive ? 'Active' : 'Inactive'}
                              color={user.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No members found for this household.
                  </Typography>
                )}
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                <strong>Created:</strong> {viewingHousehold.createdAt ? new Date(viewingHousehold.createdAt).toLocaleString() : 'N/A'}
              </Typography>
              {viewingHousehold.updatedAt && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Last Updated:</strong> {new Date(viewingHousehold.updatedAt).toLocaleString()}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseViewDialog} color="inherit">
            Close
          </Button>
                 </DialogActions>
       </Dialog>

       {/* Delete Confirmation Dialog */}
       <Dialog 
         open={deleteDialogOpen} 
         onClose={cancelDelete}
         maxWidth="sm"
         fullWidth
         PaperProps={{
           sx: {
             borderRadius: 2,
             boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
           }
         }}
       >
         <DialogTitle 
           sx={{
             backgroundColor: 'error.main',
             color: 'white',
             fontWeight: 'bold'
           }}
         >
           Delete Household
         </DialogTitle>
         <DialogContent sx={{ pt: 6 }}>
           <Box sx={{ mb: 3 }}>
             <Typography variant="h6" gutterBottom color="error.main">
               Are you sure you want to delete this household?
             </Typography>
             
             {householdToDelete && (
               <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                 <Typography variant="body1" fontWeight="medium" gutterBottom>
                   {householdToDelete.name}
                 </Typography>
                 <Typography variant="body2" color="text.secondary">
                   Code: {householdToDelete.householdCode}
                 </Typography>
                 <Typography variant="body2" color="text.secondary">
                   Members: {householdToDelete.userIds ? householdToDelete.userIds.length : 0}
                 </Typography>
               </Box>
             )}
             
             <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
               This action cannot be undone. The household and all associated data will be permanently removed.
             </Typography>
           </Box>
         </DialogContent>
         <DialogActions sx={{ p: 3, pt: 0 }}>
           <Button onClick={cancelDelete} color="inherit">
             Cancel
           </Button>
           <Button 
             onClick={confirmDelete} 
             variant="contained"
             color="error"
             sx={{
               '&:hover': {
                 backgroundColor: 'error.dark'
               }
             }}
           >
             Delete Household
           </Button>
         </DialogActions>
       </Dialog>
     </Box>
   );
 };

export default Households; 